// TechRoadmapStepper.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  //Divider,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Assessment as AssessmentIcon,
  DeveloperMode as DeveloperIcon,
  Security as SecurityIcon,
  Schedule as TimelineIcon,
  ShowChart as RoadmapIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

interface TechRoadmapStepperProps {
  productName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  }
}));

const TechRoadmapStepper: React.FC<TechRoadmapStepperProps> = ({
  productName = "Product",
  initialStep = 0,
  onComplete,
  onSubmitRFP,
}) => {
  // Add state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 3200, // Technology Assessment
    1: 3800, // Architectural Roadmap
    2: 2900, // Risk Assessment & Mitigation
    3: 2400, // Implementation & Communication
    4: 1800, // Metrics & KPIs
  });

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Define the technology roadmap steps
  const techSteps: StepData[] = [
    {
      label: 'Technology Assessment',
      description: 'Evaluate current infrastructure, tech stack, and performance gaps',
      icon: <AssessmentIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Technology Assessment for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Current Infrastructure Audit"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <DeveloperIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Evaluate system performance and bottlenecks" 
                    secondary="Assess hardware, network, and software dependencies" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <AssessmentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Competitive Technology Benchmarking" 
                    secondary="Analyze competitor infrastructure and industry standards" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
              </List>
            }
          />
        </Box>
      )
    },
    {
      label: 'Architectural Roadmap',
      description: 'Design a scalable, modular architecture and visualize it',
      icon: <RoadmapIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Architectural Roadmap for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Design & Diagrams"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Define system architecture using layered models (e.g., Domain Driven, Clean Architecture).  
                - Incorporate visual design diagrams (e.g., Mermaid diagrams) for system components and data flows.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Technology Roadmap
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Short-Term Goals
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Quick wins, proof of concepts, initial system audits.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Long-Term Vision
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Scalable architectures, integration of AI-driven automation, and future-proofing initiatives.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Risk Assessment & Mitigation',
      description: 'Identify, assess, and mitigate potential risks and vulnerabilities',
      icon: <SecurityIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Risk Assessment & Mitigation for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Risk Matrix & Analysis"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Develop a risk assessment matrix categorizing risks by likelihood and impact.
                - Identify security vulnerabilities, compliance issues, and technical debt.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Mitigation Strategies
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#FF6F00' }}>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Implement robust security protocols" 
                  secondary="Data encryption, regular audits, and compliance checks" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#FF6F00' }}>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Develop contingency plans" 
                  secondary="Risk mitigation plans and disaster recovery protocols" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Implementation & Communication',
      description: 'Outline the execution timeline and communication strategy',
      icon: <TimelineIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Implementation & Communication for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Execution Timeline"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Develop a Gantt chart outlining major milestones.
                - Define resource allocation, deadlines, and critical paths.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Communication Plan
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              - Define regular update meetings, stakeholder briefings, and risk reporting protocols.
              - Use dashboards to monitor progress and communicate updates in real time.
            </Typography>
          </ContentBox>
        </Box>
      )
    },
    { 
      label: 'Metrics & KPIs',
      description: 'Define performance metrics and key performance indicators',
      icon: <BarChartIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Metrics & KPIs for {productName}
          </GradientText>

          <EditableStepContent
            title="Performance Metrics"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Define metrics for system performance, user engagement, and business impact.
                - Use dashboards to monitor progress and adjust strategies.
              </Typography>
            }
          />

          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Key Performance Indicators (KPIs)
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              - Define KPIs for customer satisfaction, operational efficiency, and business growth.
              - Use dashboards to track progress and adjust strategies.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              <MetricChip label="Customer Satisfaction" />
              <MetricChip label="Operational Efficiency" />
              <MetricChip label="Business Growth" />
              <MetricChip label="System Uptime" />
              <MetricChip label="Response Time" />
              <MetricChip label="Error Rate" />
            </Box>
          </ContentBox>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <HolographicStepper 
        steps={techSteps}
        initialStep={initialStep}
        onComplete={onComplete}
        orientation="horizontal"
        alternativeLabel={true}
      />
    </Box>
  );
};

export default TechRoadmapStepper;